import { FALLBACK_LANGUAGE } from "../config";

const ISO_DATE_REGEX = /^(\d{4}-\d{2}-\d{2})(?:[T\s](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,6})?)?(?:Z|[+-]\d{2}:?\d{2})?)?)?$/;
const LICENSE_PLATE_PATTERN = /^[A-ZÄÖÜß]{1,3}-[A-ZÄÖÜß]{1,2}-\d{1,4}$/i;

type DisplayNameCategory = "language" | "region";

export type FieldFormatKind = "raw" | "datetime" | "language" | "boolean" | "number";

export interface FieldFormatDescriptor {
  kind: FieldFormatKind;
}

const FIELD_FORMAT_KINDS = new Set<FieldFormatKind>(["raw", "datetime", "language", "boolean", "number"]);

const DISPLAY_NAMES_CACHE = new Map<string, Intl.DisplayNames | null>();

const BOOLEAN_LABELS: Record<string, { true: string; false: string }> = {
  de: { true: "Ja", false: "Nein" },
  "de-DE": { true: "Ja", false: "Nein" },
  en: { true: "Yes", false: "No" },
  "en-US": { true: "Yes", false: "No" }
};

export function isFieldFormatDescriptor(value: unknown): value is FieldFormatDescriptor {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const kind = (value as { kind?: unknown }).kind;
  if (typeof kind !== "string") {
    return false;
  }

  return FIELD_FORMAT_KINDS.has(kind as FieldFormatKind);
}

export function coerceFieldFormat(value: unknown): FieldFormatDescriptor | undefined {
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }

    if (normalized === "licenseplate") {
      return { kind: "raw" };
    }

    if (normalized === "raw" || normalized === "datetime" || normalized === "language" || normalized === "boolean" || normalized === "number") {
      return { kind: normalized as FieldFormatKind };
    }

    return undefined;
  }

  if (isFieldFormatDescriptor(value)) {
    return value;
  }

  return undefined;
}

function pickLocalizedValue<T>(map: Record<string, T> | undefined, language: string): T | undefined {
  if (!map) {
    return undefined;
  }

  if (map[language]) {
    return map[language];
  }

  const short = language.split("-")[0];
  if (map[short]) {
    return map[short];
  }

  if (map[FALLBACK_LANGUAGE]) {
    return map[FALLBACK_LANGUAGE];
  }

  const fallbackShort = FALLBACK_LANGUAGE.split("-")[0];
  if (map[fallbackShort]) {
    return map[fallbackShort];
  }

  return undefined;
}

function formatBoolean(value: boolean, language: string): string {
  const labels = pickLocalizedValue(BOOLEAN_LABELS, language) ?? pickLocalizedValue(BOOLEAN_LABELS, FALLBACK_LANGUAGE);
  if (labels) {
    return labels[value ? "true" : "false"];
  }

  return value ? "true" : "false";
}

function normalizeLabel(label: string, locale: string): string {
  const normalized = label.replace(/\s+/g, " ").trim();
  if (normalized.length === 0) {
    return label;
  }

  const first = normalized.charAt(0);
  const upper = first.toLocaleUpperCase(locale);
  if (first === upper) {
    return normalized;
  }

  return `${upper}${normalized.slice(1)}`;
}

function getDisplayNames(locale: string, category: DisplayNameCategory): Intl.DisplayNames | null {
  const cacheKey = `${locale.toLowerCase()}::${category}`;
  if (DISPLAY_NAMES_CACHE.has(cacheKey)) {
    return DISPLAY_NAMES_CACHE.get(cacheKey) ?? null;
  }

  if (typeof Intl.DisplayNames === "undefined") {
    DISPLAY_NAMES_CACHE.set(cacheKey, null);
    return null;
  }

  try {
    const instance = new Intl.DisplayNames([locale, FALLBACK_LANGUAGE], { type: category });
    DISPLAY_NAMES_CACHE.set(cacheKey, instance);
    return instance;
  } catch {
    DISPLAY_NAMES_CACHE.set(cacheKey, null);
    return null;
  }
}

function formatLocaleCode(value: string, displayLocale: string): string | null {
  let canonical: string;
  try {
    const canonicalLocales = Intl.getCanonicalLocales(value);
    if (!canonicalLocales || canonicalLocales.length === 0) {
      return null;
    }

    [canonical] = canonicalLocales;
  } catch {
    return null;
  }

  const segments = canonical.split("-");
  const languageCode = segments[0];
  if (!languageCode) {
    return null;
  }

  const languageDisplay =
    getDisplayNames(displayLocale, "language")?.of(languageCode) ??
    getDisplayNames(FALLBACK_LANGUAGE, "language")?.of(languageCode) ??
    null;

  if (languageDisplay && languageDisplay.trim().length > 0) {
    return normalizeLabel(languageDisplay, displayLocale);
  }

  return languageCode;
}

function applyExplicitFormat(value: unknown, locale: string, format: FieldFormatDescriptor): string | undefined {
  switch (format.kind) {
    case "raw": {
      if (value === null || value === undefined) {
        return "-";
      }

      if (typeof value === "string") {
        return value.trim().length === 0 ? "-" : value;
      }

      if (value instanceof Date) {
        return value.toISOString();
      }

      return String(value);
    }
    case "datetime": {
      if (typeof value === "string") {
        return formatDateTime(value, locale);
      }

      if (value instanceof Date) {
        return formatDateTime(value.toISOString(), locale);
      }

      return undefined;
    }
    case "language": {
      if (typeof value === "string") {
        const label = formatLocaleCode(value, locale);
        if (label) {
          return label;
        }

        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : "-";
      }

      return undefined;
    }
    case "boolean": {
      if (typeof value === "boolean") {
        return formatBoolean(value, locale);
      }

      if (typeof value === "number") {
        return formatBoolean(value !== 0, locale);
      }

      if (typeof value === "string") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true" || normalized === "yes" || normalized === "ja") {
          return formatBoolean(true, locale);
        }

        if (normalized === "false" || normalized === "no" || normalized === "nein") {
          return formatBoolean(false, locale);
        }
      }

      return undefined;
    }
    case "number": {
      if (typeof value === "number") {
        return Number.isFinite(value) ? value.toLocaleString(locale) : String(value);
      }

      if (typeof value === "string") {
        const normalized = Number(value);
        if (!Number.isNaN(normalized)) {
          return Number.isFinite(normalized) ? normalized.toLocaleString(locale) : String(normalized);
        }
      }

      return undefined;
    }
    default:
      return undefined;
  }
}

export function formatResultValue(value: unknown, locale: string, format?: FieldFormatDescriptor | null): string {
  if (value === null || value === undefined) {
    return "-";
  }

  if (format) {
    const formatted = applyExplicitFormat(value, locale, format);
    if (formatted !== undefined) {
      return formatted;
    }
  }

  if (value instanceof Date) {
    return formatDateTime(value.toISOString(), locale);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return "-";
    }

    if (!format && isLicensePlate(value)) {
      return value;
    }

    if (isIsoDateString(trimmed)) {
      return formatDateTime(trimmed, locale);
    }

    const localeLabel = formatLocaleCode(trimmed, locale);
    if (localeLabel && localeLabel !== trimmed) {
      return localeLabel;
    }

    return trimmed;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value.toLocaleString(locale) : String(value);
  }

  if (typeof value === "boolean") {
    return formatBoolean(value, locale);
  }

  if (Array.isArray(value)) {
    return value.map((item) => formatResultValue(item, locale)).join(", ");
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "[Objekt]";
    }
  }

  return String(value);
}

export function formatDateTime(value: string, locale: string): string {
  try {
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) {
      return value;
    }

    const hasTimeComponent = value.includes("T");
    const formatter = new Intl.DateTimeFormat(locale, hasTimeComponent
      ? { dateStyle: "short", timeStyle: "short" }
      : { dateStyle: "short" });

    return formatter.format(date);
  } catch {
    return value;
  }
}

export function isIsoDateString(value: string): boolean {
  if (!ISO_DATE_REGEX.test(value)) {
    return false;
  }

  const parsed = Date.parse(value);
  return Number.isFinite(parsed);
}

function isLicensePlate(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 4 || trimmed.length > 16) {
    return false;
  }

  if (!/[0-9]/.test(trimmed)) {
    return false;
  }

  const normalized = trimmed.replace(/\s+/g, "-").replace(/-+/g, "-");
  return LICENSE_PLATE_PATTERN.test(normalized);
}
